import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * AI IMAGE PROMPTS:
 * 
 * step-1-take-test.jpg:
 * "Student selecting quiz on modern tablet interface, holographic quiz cards floating, 
 * AI selecting personalized questions, blue and purple glow, futuristic education, 8K"
 * 
 * step-2-analysis.jpg:
 * "AI analyzing test results with colorful charts and graphs appearing, 
 * performance dashboard with holographic elements, data visualization, purple theme, 8K"
 * 
 * step-3-voice-tutor.jpg:
 * "Student talking to AI voice assistant with sound waves and holographic teacher, 
 * microphone icon glowing, interactive learning, warm colors, 8K quality"
 * 
 * step-4-improve.jpg:
 * "Student celebrating success with upward trending graph, achievement badges floating, 
 * progress bars increasing, motivational and inspiring, gradient colors, 8K"
 */

interface Step {
  number: number;
  title: string;
  description: string;
  icon: string;
  color: string;
  imagePlaceholder: string;
  details: string[];
}

const HowItWorksAnimated: React.FC = () => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  const steps: Step[] = [
    {
      number: 1,
      title: "Take Personalized Tests",
      description: "Start with AI-curated practice tests designed specifically for your current level and target exam.",
      icon: "fas fa-clipboard-check",
      color: "#667eea",
      imagePlaceholder: "step-1-take-test.jpg",
      details: [
        "Choose from mock tests, topic tests, or custom quizzes",
        "AI adapts difficulty based on your performance",
        "Exam-like interface for real preparation",
        "Track time and manage questions efficiently"
      ]
    },
    {
      number: 2,
      title: "Get Instant AI Analysis",
      description: "Receive comprehensive performance insights within seconds with 30+ detailed metrics analyzed by AI.",
      icon: "fas fa-chart-line",
      color: "#764ba2",
      imagePlaceholder: "step-2-analysis.jpg",
      details: [
        "Subject-wise and topic-wise breakdown",
        "Identify weak areas and knowledge gaps",
        "Time management and efficiency analysis",
        "Personalized study recommendations"
      ]
    },
    {
      number: 3,
      title: "Learn with AI Tutor",
      description: "Clear your doubts instantly using voice or text with our intelligent AI tutor available 24/7.",
      icon: "fas fa-microphone-alt",
      color: "#2d9cdb",
      imagePlaceholder: "step-3-voice-tutor.jpg",
      details: [
        "Ask questions in natural language",
        "Get step-by-step explanations",
        "Understand concepts with examples",
        "Context-aware doubt resolution"
      ]
    },
    {
      number: 4,
      title: "Track & Improve",
      description: "Monitor your progress with detailed analytics, compete with peers, and watch your scores soar.",
      icon: "fas fa-trophy",
      color: "#27ae60",
      imagePlaceholder: "step-4-improve.jpg",
      details: [
        "Visual progress tracking with charts",
        "Compare performance with peers",
        "Achieve milestones and earn badges",
        "Personalized improvement roadmap"
      ]
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const stepElements = entry.target.querySelectorAll('.workflow-step-card');
            stepElements.forEach((el, index) => {
              setTimeout(() => {
                el.classList.add('animate-in');
              }, index * 200);
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={sectionRef} className="how-it-works-animated py-5" style={{ background: '#ffffff' }}>
      <div className="container py-5">
        
        {/* Section Header */}
        <div className="text-center mb-5 pb-4">
          <div className="d-inline-flex align-items-center px-4 py-2 mb-3 rounded-pill"
            style={{
              background: 'linear-gradient(135deg, #667eea15, #764ba215)',
              border: '1px solid #667eea30'
            }}>
            <i className="fas fa-route me-2 text-primary"></i>
            <span className="fw-semibold text-primary">Your Learning Journey</span>
          </div>
          <h2 className="display-4 fw-bold mb-3">
            How <span style={{ 
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>WiZiAI Works</span>
          </h2>
          <p className="lead text-muted mb-0" style={{ maxWidth: '700px', margin: '0 auto' }}>
            A simple, intelligent 4-step process that transforms your JEE/NEET preparation 
            and accelerates your path to success.
          </p>
        </div>

        {/* Timeline Steps */}
        <div className="position-relative">
          {/* Vertical Timeline Line */}
          <div className="position-absolute d-none d-lg-block" 
            style={{
              left: '50%',
              top: '0',
              bottom: '0',
              width: '3px',
              background: 'linear-gradient(to bottom, #667eea, #764ba2, #2d9cdb, #27ae60)',
              transform: 'translateX(-50%)',
              opacity: 0.3
            }}></div>

          {steps.map((step, index) => (
            <div key={step.number} className="workflow-step-card mb-5 pb-4"
              style={{ opacity: 0, transition: 'opacity 0.6s ease, transform 0.6s ease' }}>
              <div className="row align-items-center g-4">
                
                {/* Left Side - Image (alternating) */}
                {index % 2 === 0 ? (
                  <>
                    <div className="col-lg-5 offset-lg-1 order-2 order-lg-1">
                      <div className="position-relative">
                        <div className="rounded-4 overflow-hidden shadow-lg"
                          style={{ border: `3px solid ${step.color}` }}>
                          
                          {/* Step Image */}
                          <img 
                            src={`/img/home-v2/${step.imagePlaceholder}`}
                            alt={step.title}
                            className="w-100"
                            style={{ height: '350px', objectFit: 'cover' }}
                          />
                        </div>

                        {/* Floating Step Number */}
                        <div className="position-absolute" 
                          style={{ top: '-80px', right: '-50px' }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center shadow-lg"
                            style={{
                              width: '80px',
                              height: '80px',
                              background: step.color,
                              border: '5px solid white'
                            }}>
                            <span className="text-white fw-bold fs-2">{step.number}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-5 order-1 order-lg-2">
                      <div className="p-4">
                        <h3 className="fw-bold mb-3" style={{ color: step.color }}>
                          <i className={`${step.icon} me-2`}></i>
                          {step.title}
                        </h3>
                        <p className="text-muted mb-4 fs-5">
                          {step.description}
                        </p>
                        <ul className="list-unstyled">
                          {step.details.map((detail, idx) => (
                            <li key={idx} className="d-flex align-items-start mb-3">
                              <div className="flex-shrink-0">
                                <div className="d-flex align-items-center justify-content-center rounded-circle"
                                  style={{
                                    width: '30px',
                                    height: '30px',
                                    background: `${step.color}20`,
                                    border: `2px solid ${step.color}`
                                  }}>
                                  <i className="fas fa-check" 
                                    style={{ color: step.color, fontSize: '0.8rem' }}></i>
                                </div>
                              </div>
                              <span className="flex-grow-1 ms-3 text-muted">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-lg-5 offset-lg-1">
                      <div className="p-4">
                        <h3 className="fw-bold mb-3" style={{ color: step.color }}>
                          <i className={`${step.icon} me-2`}></i>
                          {step.title}
                        </h3>
                        <p className="text-muted mb-4 fs-5">
                          {step.description}
                        </p>
                        <ul className="list-unstyled">
                          {step.details.map((detail, idx) => (
                            <li key={idx} className="d-flex align-items-start mb-3">
                              <div className="flex-shrink-0">
                                <div className="d-flex align-items-center justify-content-center rounded-circle"
                                  style={{
                                    width: '30px',
                                    height: '30px',
                                    background: `${step.color}20`,
                                    border: `2px solid ${step.color}`
                                  }}>
                                  <i className="fas fa-check" 
                                    style={{ color: step.color, fontSize: '0.8rem' }}></i>
                                </div>
                              </div>
                              <span className="flex-grow-1 ms-3 text-muted">{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="col-lg-5">
                      <div className="position-relative">
                        <div className="rounded-4 overflow-hidden shadow-lg"
                          style={{ border: `3px solid ${step.color}` }}>
                          
                          {/* Step Image */}
                          <img 
                            src={`/img/home-v2/${step.imagePlaceholder}`}
                            alt={step.title}
                            className="w-100"
                            style={{ height: '350px', objectFit: 'cover' }}
                          />
                        </div>

                        {/* Floating Step Number */}
                        <div className="position-absolute" 
                          style={{ top: '-80px', left: '-50px' }}>
                          <div className="rounded-circle d-flex align-items-center justify-content-center shadow-lg"
                            style={{
                              width: '80px',
                              height: '80px',
                              background: step.color,
                              border: '5px solid white'
                            }}>
                            <span className="text-white fw-bold fs-2">{step.number}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-5 pt-4">
          <h3 className="fw-bold mb-4">Ready to Start Your Success Journey?</h3>
          <div className="d-flex flex-wrap gap-3 justify-content-center">
            <Link to="/signup" 
              className="btn btn-lg px-5 py-3 rounded-pill shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                border: 'none',
                color: 'white'
              }}>
              <i className="fas fa-rocket me-2"></i>
              Start Free Trial Now
            </Link>
            <Link to="/demo" 
              className="btn btn-outline-primary btn-lg px-5 py-3 rounded-pill">
              <i className="fas fa-play-circle me-2"></i>
              Watch How It Works
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        .workflow-step-card.animate-in {
          opacity: 1 !important;
          transform: translateY(0) !important;
        }
        
        .workflow-step-card {
          transform: translateY(30px);
        }
      `}</style>
    </div>
  );
};

export default HowItWorksAnimated;
